import "./extensions/three.convenience";
import "./extensions/three.toString";
import * as Commons from "oc-commons/Commons";
import { DefaultStageSettings } from "./DefaultStageSettings";
import * as Scenes from "./Scenes";
import * as Debugs from "oc-threejs/Debugs";
import { v3, quat } from "oc-threejs/short3";

import * as _ from "lodash";
import {
  Camera,
  Group,
  Material,
  Matrix4,
  Mesh,
  Object3D,
  Quaternion,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";

declare module "three" {
  export interface Object3D {
    __WAS_RENDERED_BEFORE__?: boolean;
    __HEARTBEAT__?: number;
    onReattachedToScene?: (stage?: AbstractStage) => void;
    onBeforeFirstRenderStage?: (stage?: AbstractStage) => void;
    onBeforeRenderStage?: () => void;
    onRemoveBecauseDependendParentMissing?: () => void;
    dependsOnObjectsArray?: Object3D[];
    raycastVisibleTags?: any;
    __pseudoChildContainer?: Group;
    parentPseudo?: Object3D;
  }
  export interface Material {
    highlightBackup?: any;
    needsUpdate: boolean;
  }
}

declare global {
  const PRODUCTION: boolean;
}

// Global variable STAGE declaration for usage within this file
declare const STAGE: AbstractStage;

/**
 * Die AbstractStage ist der zentrale Zugriffspunkt auf die Holodeck-3D-Umgebung und wird in oc-threejs an vielen
 * Stellen vorausgetzt. Die konkrete Implementierung von AbstractStage ist Stage, welche global als STAGE verfügbar ist.
 *
 * # Features der Stage
 *
 * ### Globaler Zugriffspunkt STAGE
 *
 * Die Stage als zentrales Objekt ist als STAGE global verfügbar (window.STAGE), denn normalerweise gibt es nur eine
 * Stage pro App (zwei Renderer gleichzeitig würden z.B. die meisten mobile Devices überfordern).
 *
 * ### THREE-Basisobjekte
 *
 * Im wesentlichen enthält die Stage je einen THREE.Renderer, THREE.Camera und THREE.Scene.
 * Diese zentralen Threejs Objekte werden in der Regel gemeinsam benötigt, um z.B. Bildschirm- und Weltkoordinaten
 * ineinander umzurechnen.
 *
 * ### Settings
 *
 * Weiterhin enthält die Stage allgemeine Settings, welche an diversen Stellen ausgelesen
 * werden können, siehe DefaultStageSettings.js. Die App sollte ihre Settings hier ebenfalls ablegen.
 *
 * ### Abstrakte Methoden
 *
 * Die Stage stellt folgende abstrakte Methoden bereit, die von abgeleiteten Klassen implementiert werden müssen:
 *
 * - **zoomTo(object)** zoomt die Kamera auf ein Objekt
 * - **dirty()** fordert einen neuen Rendervorgang an
 *
 * ### Object3D-Lifecycle
 *
 * Die Stage ergänzt Threejs um einen Objekt-Lifecycle. Die folgenden Callback-UniversalLoaderMethoden werden aufgerufen,
 * wenn sie auf Objekten definiert sind und die entsprechenden Ereignisse eintreten:
 *
 * - **onBeforeFirstRenderStage()** wird aufgerufen direkt bevor das Objekt zum allerersten Mal gerendert wird
 * - **onBeforeRenderStage()** wird aufgerufen direkt bevor das Objekt gerendert wird
 * - **onReattachedToScene()** wird aufgerufen, wenn ein Objekt erneut zur Szene hinzugefügt wird (auch indirekt)
 * - **onRemoveBecauseDependendParentMissing()** wird aufgerufen, wenn ein Objekt aufgrund eines
 *   dependsOnObjectsArray gelöscht wird. Objekte können als von anderen Objekten abhängig definiert werden,
 *   indem object.dependsOnObjectsArray=[otherObject] gesetzt wird.
 *
 * ### Pseudochilds
 *
 * Es gibt Methoden, um Objekte als PseudoChilds eines anderen Objektes hinzuzufügen. Ein
 * Pseudochild verhält sich wie ein Child eines anderen Objektes, ist aber nicht wirklich
 * Teil der Objekthierarchie des Parents, sondern liegt Top-Level in der Szene. Somit
 * zählen Pseudochilds z.B. nicht zur BoundingBox eines Objektes.
 *
 * ### raycastVisibleTags
 *
 * Oft möchte man für Objekte bestimmen, ob sie an bestimmten Aktionen wie drag, hover oder tap teilnehmen.
 * In der Regel werden dabei Raycasts in die Szene geschickt und geschaut, welche Objekte treffen. Der
 * Raycaster betrachtet nur Objekte, die visible sind. Mit dem Feld raycastVisibleTags kann an Objekten
 * festgelegt werden, ob sie für eine bestimmte Raycast-Aktionen visible sind oder nicht.
 * Siehe changeRaycastVisibilityDuring.
 *
 * # Code
 */
export class AbstractStage {
  public settings: any;

  // müssen von abgeleiteten Klassen gesetzt werden

  public renderer!: WebGLRenderer;
  public camera!: Camera;
  public scene!: Scene;
  public secondPassScene: Scene | null = null;

  // wird bei jedem Rendern hochgezählt, um den Lifecycle eines Objektes zu ermitteln
  public HEARTBEAT = 0;

  public _canvas: HTMLCanvasElement | null = null;

  public _objectsWithChangedOpacities: { object: Object3D; material: Material | Material[] }[] = [];

  public _materialOpacityCache = new Map<number, Material>();

  constructor(settings?: any) {
    // Convenience. Könnte bei Namenskollisionen mit umgebender Seite mittels string-replace-webpack-plugin ersetzt werden
    (window as any)["STAGE"] = this;
    this.settings = Commons.defaultsDeepSafe(settings, DefaultStageSettings);
  }

  setSecondRenderPass(on = true): void {
    if (on === (this.secondPassScene != null)) {
      return;
    }
    this.renderer.autoClear = !on;
    this.secondPassScene = on ? new Scene() : null;
  }

  get canvas(): HTMLCanvasElement {
    if (!this._canvas) {
      this._canvas = this.renderer.domElement;
    }
    return this._canvas!;
  }

  /**
   * dirty sollte überschrieben werden und einen Render-Vorgang auslösen.
   */
  dirty(): void {}

  /**
   * @deprecated benutze dirty()
   */
  setDirty(): void {
    this.dirty();
  }

  /**
   * Läßt die Kamera auf ein Objekt zeigen, so dass die BoundingSphere genau ins Sichtfeld passt
   */
  zoomTo(object: Object3D): void {}

  /**
   * muss direkt vor jedem Render-Vorgang aufgerufen werden, um den Lifecycle zu ermöglichen
   */
  onBeforeRenderStage(): void {
    const OLD_HEARTBEAT = this.HEARTBEAT;
    this.HEARTBEAT++;

    const scene = this.scene;

    scene.traverse((object: Object3D) => {
      if (object.__WAS_RENDERED_BEFORE__ && (object.__HEARTBEAT__ ?? 0) < OLD_HEARTBEAT) {
        if (object.onReattachedToScene) {
          object.onReattachedToScene(this);
        }
      }

      object.__HEARTBEAT__ = this.HEARTBEAT;

      if (object.__WAS_RENDERED_BEFORE__ == null) {
        object.__WAS_RENDERED_BEFORE__ = true;
        if (object.onBeforeFirstRenderStage) {
          object.onBeforeFirstRenderStage(this);
        }
      }
    });

    // onBeforeRenderStage ist jetzt THREE.Object3D - Basisfunktionalität!
    Scenes.filter(scene, (o: Object3D) => o.onBeforeRenderStage !== undefined).forEach(
      (o: Object3D) => o.onBeforeRenderStage!(),
    );
    if (this.secondPassScene) {
      Scenes.filter(
        this.secondPassScene,
        (o: Object3D) => o.onBeforeRenderStage !== undefined,
      ).forEach((o: Object3D) => o.onBeforeRenderStage!());
    }

    if (typeof PRODUCTION === "undefined" ? true : !PRODUCTION) {
      if (this.settings.showDebugNotANumberInSceneError) {
        this.warnFishyObjects();
      }
    }

    // entferne Objekte, wenn sie von anderen, aber fehlenden Objekten abhängen
    Scenes.filter(
      scene,
      (x: Object3D) =>
        !!x.dependsOnObjectsArray &&
        !x.dependsOnObjectsArray.every((y) => y.__HEARTBEAT__ === this.HEARTBEAT),
    ).forEach((x: Object3D) => {
      if (x.onRemoveBecauseDependendParentMissing) {
        x.onRemoveBecauseDependendParentMissing();
      }
      scene.remove(x);
    });

    this._traverseOpacityBeforeRender();
  }

  /** */
  onAfterRenderStage(): void {
    this._traverseOpacityAfterRender();
  }

  warnFishyObjects(): void {
    const isNumberOk = (x: any) => !Number.isNaN(x) && Number.isFinite(x);
    const isVectorOk = (v: any) => isNumberOk(v.x) && isNumberOk(v.y) && isNumberOk(v.z);
    const isQuaternionOk = (q: any) =>
      isNumberOk(q.x) && isNumberOk(q.y) && isNumberOk(q.z) && isNumberOk(q.w);
    const isMatrixOk = (q: any) => q.elements.every(isNumberOk);
    const isOk = (g: any) =>
      (g instanceof Vector3 && isVectorOk(g)) ||
      (g instanceof Quaternion && isQuaternionOk(g)) ||
      (g instanceof Matrix4 && isMatrixOk(g));

    const scene = this.scene;
    scene.traverse((object: Object3D) => {
      if (!isOk(object.matrix)) {
        if (!isOk(object.position)) {
          console.error(
            "The position of an object3d is fishy! " + object + " " + object.position,
            object,
          );
        } else if (!isOk(object.scale)) {
          console.error(
            "The scale of an object3d is fishy! " + object + " " + object.scale,
            object,
          );
        } else if (!isOk(object.quaternion)) {
          console.error(
            "The quaternion of an object3d is fishy! " + object + " " + object.quaternion,
            object,
          );
        } else {
          console.error(
            "The matrix of an object3d is fishy! " + object + " " + object.matrix,
            object,
          );
        }
      }
    });

    Debugs.checkParentChild3d(scene);
  }

  /**
   */
  _traverseOpacityBeforeRender(): void {
    this._objectsWithChangedOpacities.length = 0;
    const cloneMaterial = (material: Material, opacity: number): Material => {
      const moId = material.id * 1000 + Math.floor(opacity * 255);
      let clone = this._materialOpacityCache.get(moId);
      if (clone == null) {
        clone = material.clone();
        clone.opacity = opacity;
        clone.transparent = true;
        clone.needsUpdate = true;
        this._materialOpacityCache.set(moId, clone);
      }
      return clone;
    };
    const traverse = (object: Object3D, opacity: number) => {
      if (object.visible) {
        const mat = object instanceof Mesh ? object.material : undefined;
        let objOpacity = (object as any).opacity; // some objects have opacity?
        // object.opacity is not standard Object3D property unless augmented or specific type.
        // JS code: opacity *= object.opacity == null ? 1 : object.opacity;
        // Maybe it means material opacity? No, recursive.
        // Assuming custom property.

        opacity *= objOpacity == null ? 1 : objOpacity;

        if (mat && opacity < 1) {
          this._objectsWithChangedOpacities.push({ object, material: mat });
          if (Array.isArray(mat)) {
            (object as Mesh).material = mat.map((material) => cloneMaterial(material, opacity));
          } else {
            (object as Mesh).material = cloneMaterial(mat, opacity);
          }
        }
        for (let child of object.children) {
          traverse(child, opacity);
        }
      }
    };
    traverse(this.scene, 1);
  }

  /**
   */
  _traverseOpacityAfterRender(): void {
    this._objectsWithChangedOpacities.forEach(({ object, material }) => {
      (object as Mesh).material = material;
    });
    this._objectsWithChangedOpacities.length = 0;
  }

  /**
   * gibt den aktuellen Heartbeat (Anzahl der Rendering-Vorgänge) zurück
   */
  getHeartbeat(): number {
    return this.HEARTBEAT;
  }

  /**
   * Fügt einem Object ein DebugObject hinzu. Ein DebugObject ist ein PseudoChild,
   * hängt also nicht in der Objecthierarchie von Object, folgt ihm aber und nimmt
   * auch nicht an Raycasts Teil.
   */
  addAsDebugObject(object: Object3D, debugObject: Object3D): void {
    const offset = debugObject.position.clone();
    debugObject.raycastVisibleTags = [];
    debugObject.dependsOnObjectsArray = [object];
    debugObject.onBeforeRenderStage = () => {
      debugObject.position.copy(object.getWorldPosition(v3()).add(offset));
      debugObject.quaternion.copy(object.getWorldQuaternion(quat()));
    };
    this.scene.add(debugObject);
  }

  /**
   * ein PseudoChild verhält sich wie ein Kindobjekt, liegt aber nicht
   * im Objekttree des Parents, sondern Top-Level in der Scene.
   * Vorteil: Ein PseudoChild wird nicht zur BoundingBox des Parents gerechnet, und
   * man kann definieren, welchen Eigenschaften des Parents es folgt
   * (position, quaternion und scale).
   */
  addAsPseudoChild(parent: Object3D, pseudoChild: Object3D, options?: any): void {
    const container = this.createPseudoChildContainer(parent, options);
    container.add(pseudoChild);
    //options.onFollow(parent,container,pseudoChild);
  }

  /**
   * erzeugt einen PseudoChildContainer, welcher Pseudochilds aufnehmen kann
   */
  createPseudoChildContainer(parent: Object3D, options: any): Group {
    options = Commons.defaultsDeepSafe({}, options, {
      followPosition: true,
      followQuaternion: true,
      followScale: true,
      onFollow: (parent: Object3D, container: Object3D) => {},
    });
    const container = new Group();
    container.name = parent.name + "_PseudoChildContainer";
    container.parentPseudo = parent;
    container.raycastVisibleTags = [];
    container.dependsOnObjectsArray = [parent];
    parent.onReattachedToScene = () => {
      this.scene.add(parent.__pseudoChildContainer!);
      delete parent.__pseudoChildContainer;
    };
    container.onRemoveBecauseDependendParentMissing = () => {
      parent.__pseudoChildContainer = container;
    };
    container.onBeforeRenderStage = () => {
      if (options.followPosition) {
        container.position.copy(parent.getWorldPosition(v3()));
      }
      if (options.followQuaternion) {
        container.quaternion.copy(parent.getWorldQuaternion(quat()));
      }
      if (options.followScale) {
        container.scale.copy(parent.getWorldScale(v3())); // getWorldScale needs target in newer THREE? CHECK
      }
      options.onFollow(parent, container);
    };
    this.scene.add(container);
    return container;
  }

  /**
   * Setzt während der Ausführung von callback vorübergehend alle Objekte mit vorhandenem raycastVisibleTags - Array sichtbar,
   * falls das raycastVisibleTags - Array das Tag enthält, und unsichtbar, falls das raycastVisibleTags - Array das Tag nicht enthält.
   */
  changeRaycastVisibilityDuring<T>(tag: string, callback: () => T): T {
    const objects: [Object3D, boolean][] = [];

    this.scene.traverse((o: Object3D) => {
      if (o.raycastVisibleTags != null) {
        objects.push([o, o.visible]);
        o.visible = o.raycastVisibleTags.includes(tag);
      }
    });

    const result = callback();

    objects.forEach(([o, oldVisible]) => {
      o.visible = oldVisible;
    });

    return result;
  }

  /**
   * convenience-getter, gibt an, wie viel Einheiten ein Meter in der Unit der Stage ist
   */
  get mInUnit(): number {
    return STAGE.settings.ruler.mInUnit;
  }
}
